<template>
  <div class="sig-field">
    <div v-if="savedUrl && !signing" class="sig-signed" :class="{ invalid }">
      <img :src="savedUrl" alt="Firma" class="sig-image" />
      <button type="button" class="link-btn" @click="signAgain">Firmar de nuevo</button>
    </div>

    <div v-else class="sig-draw">
      <div class="sig-canvas-wrap" :class="{ invalid }">
        <canvas
          ref="canvas"
          class="sig-canvas"
          @mousedown="start"
          @mousemove="move"
          @mouseup="end"
          @mouseleave="end"
          @touchstart.prevent="start"
          @touchmove.prevent="move"
          @touchend.prevent="end"
        ></canvas>
        <span v-if="!hasDrawing" class="sig-placeholder">Firme aquí con el mouse o el dedo</span>
      </div>
      <div class="sig-actions">
        <button type="button" class="link-btn" :disabled="!hasDrawing" @click="clear">
          Limpiar
        </button>
      </div>
    </div>
    <div v-if="invalid" class="field-error">Debe firmar para continuar.</div>
  </div>
</template>

<script>
export default {
  name: "SignaturePad",
  props: {
    savedUrl: { type: String, default: null },
    invalid: { type: Boolean, default: false },
  },
  data() {
    return {
      signing: !this.savedUrl,
      hasDrawing: false,
      drawing: false,
      ctx: null,
    };
  },
  mounted() {
    this.setupCanvas();
    window.addEventListener("resize", this.setupCanvas);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setupCanvas);
  },
  watch: {
    savedUrl(val) {
      if (val) this.signing = false;
    },
    signing(val) {
      if (val) this.$nextTick(this.setupCanvas);
    },
    hasDrawing(val) {
      this.$emit("drawing-change", val);
    },
  },
  methods: {
    setupCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#16223d";
      this.ctx = ctx;
      this.hasDrawing = false;
    },
    posFromEvent(e) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const point = e.touches && e.touches.length ? e.touches[0] : e;
      return { x: point.clientX - rect.left, y: point.clientY - rect.top };
    },
    start(e) {
      if (!this.ctx) return;
      this.drawing = true;
      const { x, y } = this.posFromEvent(e);
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
    },
    move(e) {
      if (!this.drawing || !this.ctx) return;
      const { x, y } = this.posFromEvent(e);
      this.ctx.lineTo(x, y);
      this.ctx.stroke();
      this.hasDrawing = true;
    },
    end() {
      this.drawing = false;
    },
    clear() {
      const canvas = this.$refs.canvas;
      if (canvas && this.ctx) {
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      this.hasDrawing = false;
    },
    // Called by the parent (via $ref) at submit time, instead of a separate save button.
    captureBlob() {
      return new Promise((resolve) => {
        const canvas = this.$refs.canvas;
        if (!canvas || !this.hasDrawing) {
          resolve(null);
          return;
        }
        canvas.toBlob((blob) => resolve(blob || null), "image/png");
      });
    },
    signAgain() {
      this.signing = true;
      this.$emit("clear");
    },
  },
};
</script>

<style scoped>
/* Drawn in the portal's look (Declaración, edited in place): a soft dashed pad, its label being the section's title. */
.sig-field {
  display: flex;
  flex-direction: column;
  max-width: 480px;
}
.sig-canvas-wrap {
  position: relative;
  border: 1.5px dashed #cfd4de;
  border-radius: 14px;
  background: #fafbfc;
  overflow: hidden;
  transition: border-color 0.15s, background-color 0.15s;
}
.sig-canvas-wrap:hover {
  border-color: #e6b65c;
  background: #fffdf8;
}
.sig-canvas-wrap.invalid {
  border-color: #f2c0c0;
  background: #fff8f8;
}
.sig-canvas {
  display: block;
  width: 100%;
  height: 200px;
  touch-action: none;
  cursor: crosshair;
}
.sig-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  font: 500 14px "Geist", system-ui, sans-serif;
  color: #a7adbb;
  text-align: center;
  pointer-events: none;
}
.sig-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}
.link-btn {
  border: none;
  background: none;
  padding: 0;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #6b7283;
  cursor: pointer;
}
.link-btn:hover:not(:disabled) {
  color: #c42b2b;
}
.link-btn:disabled {
  color: #c9ced9;
  cursor: not-allowed;
}
.sig-signed {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border: 1px solid #e3e6ed;
  border-radius: 14px;
  background: #fff;
}
.sig-signed.invalid {
  border-color: #f2c0c0;
}
.sig-image {
  height: 70px;
  max-width: 220px;
  object-fit: contain;
  border-radius: 8px;
  background: #fafbfc;
  border: 1px solid #eceef3;
}
.field-error {
  margin-top: 8px;
  font: 500 12px/1.4 "Geist", system-ui, sans-serif;
  color: #c42b2b;
}
</style>
